"use client";

import {JSX, ReactNode} from "react";

import {useSnapshot} from "valtio";

import {useScrollIntoView} from "@/shared/hooks/dom/use-scroll-into-view";
import {useTranslation} from "@/shared/i18n";
import {type CheckboxInstance} from "@/shared/models/form/fields";

import {type FormFieldSize} from "./types";

import "./styles/checkbox-field.scss";

type CheckboxVariant = "primary" | "secondary";

interface CheckboxFieldProps {
    model: CheckboxInstance;
    /** Plain text label (ignored if labelContent is set) */
    label?: string;
    /** Custom label content (e.g. text with links); overrides label when set */
    labelContent?: ReactNode;
    className?: string;
    size?: FormFieldSize;
    children?: ReactNode;
    variant?: CheckboxVariant;
}

export function CheckboxField({model, label, labelContent, className, size, variant}: CheckboxFieldProps): JSX.Element {
    const {t} = useTranslation("validation");
    const snapshot = useSnapshot(model);
    const ref = useScrollIntoView<HTMLLabelElement>(model);

    const handleChange = (): void => {
        model.toggle();
    };

    const sizeClass = size ? `checkbox-field--${size}` : "";
    const variantClass = variant ? `checkbox-field--${variant}` : "";

    return (
        <div className={`checkbox-field ${sizeClass} ${variantClass} ${className || ""}`}>
            <label ref={ref} className={`checkbox-field__label ${snapshot.disabled ? "disabled" : ""}`}>
                <input
                    type="checkbox"
                    checked={snapshot.checked}
                    onChange={handleChange}
                    onBlur={() => model.onBlur()}
                    onFocus={() => model.onFocus()}
                    disabled={snapshot.disabled}
                    className="checkbox-field__input"
                />

                <span className={`checkbox-field__box ${snapshot.checked ? "checked" : ""}`}>
                    {snapshot.checked && (
                        <svg className="checkbox-field__checkmark" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 5L4.5 8.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </span>

                {(labelContent !== undefined ? labelContent : label) && (
                    <span className="checkbox-field__text">
                        {labelContent !== undefined ? labelContent : label} {model.isRequired && <span className="checkbox-field__required">*</span>}
                    </span>
                )}
            </label>

            {snapshot.shouldShowError && snapshot.error && (
                <span className="checkbox-field__error-text">
                    {t(snapshot.error.message as Parameters<typeof t>[0], snapshot.error.messageParams)}
                </span>
            )}
        </div>
    );
}
